import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type AuditRow, type FaciliooWebhookQueueRow, type FaciliooWebhookRejectRow, type ImportJobRow, type VoteProtocolRow } from "../app/api";
import { ServerDataTable } from "../components/tables/ServerDataTable";

const importLabel = (value: ImportJobRow["import_typ"]) => {
  if (value === "objects") return "Objekte";
  if (value === "units") return "Einheiten";
  if (value === "owners") return "Eigentümer";
  return "Zuordnungen";
};

const statusLabel = (value: ImportJobRow["status"]) => {
  if (value === "uploaded") return "Hochgeladen";
  if (value === "validated") return "Validiert";
  if (value === "failed") return "Fehlerhaft";
  return "Importiert";
};

const statusBadgeClass = (value: ImportJobRow["status"]) => {
  if (value === "uploaded") return "badge import-uploaded";
  if (value === "validated") return "badge import-validated";
  if (value === "failed") return "badge import-failed";
  return "badge import-imported";
};

const importSourceLabel = (row: ImportJobRow) =>
  row.filename?.toLowerCase().startsWith("facilioo-") ? "Facilioo-Dateiimport" : "Manueller Dateiimport";

const faciliooSyncStatus = (action: string) => (action.endsWith("_failed") ? "Fehler" : "OK");

const faciliooSyncTrigger = (action: string) => {
  if (action.includes("_auto_")) return "Automatisch";
  if (action.includes("_manual_")) return "Manuell";
  return "Unbekannt";
};

const faciliooWebhookStatus = (row: FaciliooWebhookQueueRow) => {
  if (row.db_state === "processed" || row.db_processed === true) return "Processed";
  if (row.db_state === "processing") return "Processing";
  if (row.db_state === "queued") return "Queued";
  if (row.action.endsWith("_deduplicated")) return "Deduplicated";
  return "Queued";
};

const faciliooWebhookStatusBadgeClass = (row: FaciliooWebhookQueueRow) => {
  if (row.db_state === "processed" || row.db_processed === true) return "badge import-imported";
  if (row.db_state === "processing") return "badge import-validated";
  if (row.db_state === "queued") return "badge import-uploaded";
  if (row.action.endsWith("_deduplicated")) return "badge import-validated";
  return "badge import-uploaded";
};

const readWebhookChange = (row: AuditRow, key: string) => {
  if (!row.changes_json || typeof row.changes_json !== "object") return "-";
  const value = (row.changes_json as Record<string, unknown>)[key];
  if (value === undefined || value === null) return "-";
  const text = String(value).trim();
  return text || "-";
};

const webhookDbStatusLabel = (row: FaciliooWebhookQueueRow) => {
  const reasonRaw = String(row.db_error_message ?? "").trim();
  const reason = reasonRaw.toLowerCase();
  const blockedReasonLabel =
    reason === "blocked_by_sync_running"
      ? "blockiert: anderer Sync läuft"
      : reason === "blocked_by_webhook_disabled"
        ? "blockiert: Webhook deaktiviert"
        : reason === "blocked_by_missing_facilioo_api_key"
          ? "blockiert: API-Key fehlt"
          : "";
  if (row.db_state === "processed" || row.db_processed === true) return "Ja";
  if (row.db_state === "queued") return blockedReasonLabel ? `Nein (queued, ${blockedReasonLabel})` : "Nein (queued)";
  if (row.db_state === "processing") return "Nein (processing)";
  return "-";
};

type VoteSortField = "timestamp" | "beschlussnummer" | "objektnummer" | "einheit_refs" | "owner_fullname" | "vote_value";
type LogSectionKey =
  | "voteGlobal"
  | "voteQrRecent"
  | "imports"
  | "faciliooSync"
  | "faciliooWebhookQueue"
  | "faciliooWebhookRejects"
  | "audit";

const normalizeMojibake = (value: unknown) => {
  const text = String(value ?? "");
  return text
    .replace(/ÃƒÂ¤/g, "ä")
    .replace(/ÃƒÂ„/g, "Ä")
    .replace(/ÃƒÂ¶/g, "ö")
    .replace(/ÃƒÂ–/g, "Ö")
    .replace(/ÃƒÂ¼/g, "ü")
    .replace(/ÃƒÂœ/g, "Ü")
    .replace(/ÃƒÂŸ/g, "ß")
    .replace(/Ã¤/g, "ä")
    .replace(/Ã„/g, "Ä")
    .replace(/Ã¶/g, "ö")
    .replace(/Ã–/g, "Ö")
    .replace(/Ã¼/g, "ü")
    .replace(/Ãœ/g, "Ü")
    .replace(/ÃŸ/g, "ß")
    .replace(/Â/g, "")
    .replace(/â€“/g, "–")
    .replace(/â€”/g, "—")
    .replace(/â€ž/g, "„")
    .replace(/â€œ/g, "“");
};

const extractUnitNumbers = (value: unknown) => {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  const parts = text
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
  const units = parts
    .map((part) => {
      const slashMatch = part.match(/\/\s*([0-9]{1,6})\b/);
      if (slashMatch?.[1]) return slashMatch[1];
      const plainNumber = part.match(/\b([0-9]{1,6})\b/);
      return plainNumber?.[1] ?? "";
    })
    .filter(Boolean)
    .map((unit) => (/^\d+$/.test(unit) ? unit.padStart(4, "0") : unit));
  return units.length ? Array.from(new Set(units)).join(" | ") : "-";
};

const voteBadgeLabel = (value: VoteProtocolRow["vote_value"]) => {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "ja") return "Ja";
  if (v === "nein") return "Nein";
  if (v === "enthaltung") return "Enthaltung";
  if (v === "offen") return "Offen";
  return normalizeMojibake(value || "-");
};

const voteBadgeClass = (value: VoteProtocolRow["vote_value"]) => {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "ja") return "badge vote-ja";
  if (v === "nein") return "badge vote-nein";
  if (v === "enthaltung") return "badge vote-enthaltung";
  if (v === "offen") return "badge vote-offen";
  return "badge";
};

const formatVoteEventType = (value: unknown) => {
  const raw = normalizeMojibake(value).trim();
  if (!raw) return "-";
  const normalized = raw
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (normalized === "stimmänderung" || normalized === "stimmanderung") return "Stimmänderung";
  if (normalized === "erstmalige stimmabgabe") return "Erstmalige Stimmabgabe";
  return normalized
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ");
};

const logsTitleIcon = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5.2 19.2h13.6M7.2 19.2V11M12 19.2V7.8M16.8 19.2v-5.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

export const LogsPage = () => {
  const isFrameworkV2 =
    typeof document !== "undefined" && document.body.classList.contains("framework-v2");
  const [auditPage, setAuditPage] = useState(1);
  const [importPage, setImportPage] = useState(1);
  const [syncPage, setSyncPage] = useState(1);
  const [webhookPage, setWebhookPage] = useState(1);
  const [webhookRejectPage, setWebhookRejectPage] = useState(1);
  const [votePage, setVotePage] = useState(1);
  const [syncStatusFilter, setSyncStatusFilter] = useState<"all" | "ok" | "error">("all");
  const [syncTriggerFilter, setSyncTriggerFilter] = useState<"all" | "manual" | "auto">("all");
  const [webhookStatusFilter, setWebhookStatusFilter] = useState<"all" | "queued" | "deduplicated">("all");
  const [webhookRejectStatusFilter, setWebhookRejectStatusFilter] = useState<"all" | "400" | "401" | "500" | "503">("all");
  const [voteSearch, setVoteSearch] = useState("");
  const [voteResolutionFilter, setVoteResolutionFilter] = useState("");
  const [voteObjectFilter, setVoteObjectFilter] = useState("");
  const [voteUnitFilter, setVoteUnitFilter] = useState("");
  const [voteOwnerFilter, setVoteOwnerFilter] = useState("");
  const [voteValueFilter, setVoteValueFilter] = useState<"all" | "ja" | "nein" | "enthaltung" | "offen">("all");
  const [voteSortBy, setVoteSortBy] = useState<VoteSortField>("timestamp");
  const [voteSortDir, setVoteSortDir] = useState<"asc" | "desc">("desc");
  const [collapsedSections, setCollapsedSections] = useState<Record<LogSectionKey, boolean>>({
    voteGlobal: true,
    voteQrRecent: true,
    imports: true,
    faciliooSync: true,
    faciliooWebhookQueue: true,
    faciliooWebhookRejects: true,
    audit: true
  });
  const toggleSection = (key: LogSectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-logs"],
    queryFn: () => api.uiSettings()
  });
  const faciliooEnabled = uiSettings?.management?.faciliooEnabled !== false;

  const { data: auditData } = useQuery({
    queryKey: ["logs-audit-rows", auditPage],
    queryFn: () => api.auditLogs({ page: auditPage, pageSize: 50 })
  });

  const { data: importsData } = useQuery({
    queryKey: ["logs-imports", importPage],
    queryFn: () => api.imports({ page: importPage, pageSize: 25 })
  });

  const { data: faciliooSyncData } = useQuery({
    queryKey: ["logs-facilioo-sync", syncPage, syncStatusFilter, syncTriggerFilter],
    queryFn: () =>
      api.faciliooSyncLogs({
        page: syncPage,
        pageSize: 25,
        status: syncStatusFilter === "all" ? undefined : syncStatusFilter,
        trigger: syncTriggerFilter === "all" ? undefined : syncTriggerFilter
      }),
    enabled: faciliooEnabled
  });

  const { data: faciliooWebhookData } = useQuery({
    queryKey: ["logs-facilioo-webhooks", webhookPage, webhookStatusFilter],
    queryFn: () =>
      api.faciliooWebhookLogs({
        page: webhookPage,
        pageSize: 25,
        status: webhookStatusFilter === "all" ? undefined : webhookStatusFilter
      }),
    enabled: faciliooEnabled
  });

  const { data: faciliooWebhookRejectData } = useQuery({
    queryKey: ["logs-facilioo-webhook-rejects", webhookRejectPage, webhookRejectStatusFilter],
    queryFn: () =>
      api.faciliooWebhookRejectLogs({
        page: webhookRejectPage,
        pageSize: 25,
        status: webhookRejectStatusFilter === "all" ? undefined : webhookRejectStatusFilter
      }),
    enabled: faciliooEnabled
  });

  const { data: voteProtocolData } = useQuery({
    queryKey: [
      "logs-vote-protocol",
      votePage,
      voteSearch,
      voteResolutionFilter,
      voteObjectFilter,
      voteUnitFilter,
      voteOwnerFilter,
      voteValueFilter,
      voteSortBy,
      voteSortDir
    ],
    queryFn: () =>
      api.voteProtocols({
        page: votePage,
        pageSize: 25,
        search: voteSearch.trim() || undefined,
        resolutionId: voteResolutionFilter.trim() || undefined,
        objectRef: voteObjectFilter.trim() || undefined,
        unit: voteUnitFilter.trim() || undefined,
        owner: voteOwnerFilter.trim() || undefined,
        voteValue: voteValueFilter === "all" ? undefined : voteValueFilter,
        sortBy: voteSortBy,
        sortDir: voteSortDir
      })
  });

  const { data: qrRecentData } = useQuery({
    queryKey: ["logs-vote-protocol-qr-recent"],
    queryFn: () =>
      api.voteProtocols({
        page: 1,
        pageSize: 10,
        sortBy: "timestamp",
        sortDir: "desc",
        submissionChannel: "analog_scan"
      })
  });

  return (
    <div className={isFrameworkV2 ? "resolutions-v2-page logs-v2-page" : undefined}>
      {isFrameworkV2 ? (
        <section className="page-header panel resolutions-v2-page-header logs-v2-page-header">
          <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
            <span className="resolutions-v2-title-icon" aria-hidden="true">{logsTitleIcon}</span>
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Protokolle</span>
              <span className="resolutions-v2-title-sub">Stimmabgaben, Importe und Systemereignisse</span>
            </span>
          </h2>
        </section>
      ) : null}
      <section className="panel logs-v2-section">
        <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Stimmabgabe-Protokoll (Global)</h2>
          <button type="button" onClick={() => toggleSection("voteGlobal")} aria-expanded={!collapsedSections.voteGlobal}>
            {collapsedSections.voteGlobal ? "Ausklappen" : "Einklappen"}
          </button>
        </div>
        {!collapsedSections.voteGlobal ? <>
        <div className="panel logs-v2-subsection" style={{ marginBottom: 12, marginTop: 12 }}>
          <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Zuletzt per QR erfasst</h3>
            <button type="button" onClick={() => toggleSection("voteQrRecent")} aria-expanded={!collapsedSections.voteQrRecent}>
              {collapsedSections.voteQrRecent ? "Ausklappen" : "Einklappen"}
            </button>
          </div>
          {!collapsedSections.voteQrRecent ? <div style={{ marginTop: 8 }}>
          <ServerDataTable
            columns={[
              { key: "timestamp", label: "Zeitpunkt" },
              {
                key: "beschlussnummer",
                label: "Beschluss",
                align: "center" as const,
                render: (row: VoteProtocolRow) => {
                  const label = row.beschlussnummer || row.resolution_id || "-";
                  if (row.resolution_id) {
                    return (
                      <Link className="badge resolution-number" to={`/resolutions/${row.resolution_id}`} title={normalizeMojibake(label)}>
                        {normalizeMojibake(label)}
                      </Link>
                    );
                  }
                  return <span className="badge resolution-number">{normalizeMojibake(label)}</span>;
                }
              },
              {
                key: "objektnummer",
                label: "Objekt",
                align: "center" as const,
                render: (row: VoteProtocolRow) => {
                  const objectNumber = normalizeMojibake(row.objektnummer || "-");
                  if (objectNumber !== "-") {
                    return (
                      <Link className="badge object-number" to={`/objects?search=${encodeURIComponent(objectNumber)}`} title={objectNumber}>
                        {objectNumber}
                      </Link>
                    );
                  }
                  return <span className="badge object-number">-</span>;
                }
              },
              {
                key: "owner_fullname",
                label: "Eigentümer",
                render: (row: VoteProtocolRow) => normalizeMojibake(row.owner_fullname || row.eigentuemer_id || "-")
              },
              {
                key: "vote_value",
                label: "Stimmabgabe",
                align: "center" as const,
                render: (row: VoteProtocolRow) => <span className={voteBadgeClass(row.vote_value)}>{voteBadgeLabel(row.vote_value)}</span>
              },
              { key: "auth_user_name", label: "Erfasst von", render: (row: VoteProtocolRow) => normalizeMojibake(row.auth_user_name || "-") },
              { key: "client_ip", label: "IP" }
            ]}
            rows={qrRecentData?.data ?? []}
            total={(qrRecentData?.data ?? []).length}
            page={1}
            pageSize={qrRecentData?.meta.pageSize ?? 10}
            onPageChange={() => {
              // Intentionally fixed to most recent 10 rows.
            }}
          />
          </div> : null}
        </div>
        <div className="logs-filter-grid">
          <input
            value={voteSearch}
            onChange={(e) => {
              setVoteSearch(e.target.value);
              setVotePage(1);
            }}
            placeholder="Suche (frei)"
          />
          <input
            value={voteResolutionFilter}
            onChange={(e) => {
              setVoteResolutionFilter(e.target.value);
              setVotePage(1);
            }}
            placeholder="Beschluss-ID/-Nr."
          />
          <input
            value={voteObjectFilter}
            onChange={(e) => {
              setVoteObjectFilter(e.target.value);
              setVotePage(1);
            }}
            placeholder="Objekt"
          />
          <input
            value={voteUnitFilter}
            onChange={(e) => {
              setVoteUnitFilter(e.target.value);
              setVotePage(1);
            }}
            placeholder="Einheit"
          />
          <input
            value={voteOwnerFilter}
            onChange={(e) => {
              setVoteOwnerFilter(e.target.value);
              setVotePage(1);
            }}
            placeholder="Eigentümer"
          />
          <select
            value={voteValueFilter}
            onChange={(e) => {
              setVoteValueFilter(e.target.value as "all" | "ja" | "nein" | "enthaltung" | "offen");
              setVotePage(1);
            }}
          >
            <option value="all">Alle Stimmabgaben</option>
            <option value="ja">Ja</option>
            <option value="nein">Nein</option>
            <option value="enthaltung">Enthaltung</option>
            <option value="offen">Offen</option>
          </select>
          <select
            value={voteSortBy}
            onChange={(e) => {
              setVoteSortBy(e.target.value as VoteSortField);
              setVotePage(1);
            }}
          >
            <option value="timestamp">Sortierung: Zeitpunkt</option>
            <option value="beschlussnummer">Sortierung: Beschluss</option>
            <option value="objektnummer">Sortierung: Objekt</option>
            <option value="einheit_refs">Sortierung: Einheit</option>
            <option value="owner_fullname">Sortierung: Eigentümer</option>
            <option value="vote_value">Sortierung: Stimmabgabe</option>
          </select>
          <select
            value={voteSortDir}
            onChange={(e) => {
              setVoteSortDir(e.target.value as "asc" | "desc");
              setVotePage(1);
            }}
          >
            <option value="desc">Absteigend</option>
            <option value="asc">Aufsteigend</option>
          </select>
        </div>
        <ServerDataTable
          columns={[
            { key: "timestamp", label: "Zeitpunkt" },
            {
              key: "beschlussnummer",
              label: "Beschluss",
              align: "center" as const,
              render: (row: VoteProtocolRow) => {
                const label = row.beschlussnummer || row.resolution_id || "-";
                if (row.resolution_id) {
                  return (
                    <Link className="badge resolution-number" to={`/resolutions/${row.resolution_id}`} title={normalizeMojibake(label)}>
                      {normalizeMojibake(label)}
                    </Link>
                  );
                }
                return <span className="badge resolution-number">{normalizeMojibake(label)}</span>;
              }
            },
            {
              key: "objektnummer",
              label: "Objekt",
              align: "center" as const,
              render: (row: VoteProtocolRow) => {
                const objectNumber = normalizeMojibake(row.objektnummer || "-");
                const objectId = String((row as unknown as { object_id?: string; objectId?: string }).object_id ?? (row as unknown as { object_id?: string; objectId?: string }).objectId ?? "").trim();
                if (objectId) {
                  return (
                    <Link className="badge object-number" to={`/objects/${objectId}`} title={objectNumber}>
                      {objectNumber}
                    </Link>
                  );
                }
                if (objectNumber !== "-") {
                  return (
                    <Link className="badge object-number" to={`/objects?search=${encodeURIComponent(objectNumber)}`} title={objectNumber}>
                      {objectNumber}
                    </Link>
                  );
                }
                return <span className="badge object-number">-</span>;
              }
            },
            {
              key: "einheit_refs",
              label: "Einheit",
              align: "center" as const,
              render: (row: VoteProtocolRow) => extractUnitNumbers(row.einheit_refs)
            },
            {
              key: "owner_fullname",
              label: "Eigentümer",
              render: (row: VoteProtocolRow) => normalizeMojibake(row.owner_fullname || row.eigentuemer_id || "-")
            },
            {
              key: "vote_value",
              label: "Stimmabgabe",
              align: "center" as const,
              render: (row: VoteProtocolRow) => <span className={voteBadgeClass(row.vote_value)}>{voteBadgeLabel(row.vote_value)}</span>
            },
            { key: "vote_event_type", label: "Ereignis", render: (row: VoteProtocolRow) => formatVoteEventType(row.vote_event_type) },
            { key: "submission_channel", label: "Kanal", render: (row: VoteProtocolRow) => normalizeMojibake(row.submission_channel || "-") },
            { key: "auth_mode", label: "Authentifizierung", render: (row: VoteProtocolRow) => normalizeMojibake(row.auth_mode || "-") },
            { key: "auth_user_name", label: "Benutzer", render: (row: VoteProtocolRow) => normalizeMojibake(row.auth_user_name || "-") },
            { key: "client_ip", label: "IP" }
          ]}
          rows={voteProtocolData?.data ?? []}
          total={voteProtocolData?.meta.total ?? 0}
          page={votePage}
          pageSize={voteProtocolData?.meta.pageSize ?? 25}
          onPageChange={setVotePage}
        />
        </> : null}
      </section>

      <section className="panel logs-v2-section">
        <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Manuelle Importe</h2>
          <button type="button" onClick={() => toggleSection("imports")} aria-expanded={!collapsedSections.imports}>
            {collapsedSections.imports ? "Ausklappen" : "Einklappen"}
          </button>
        </div>
        {!collapsedSections.imports ? <div style={{ marginTop: 12 }}>
        <ServerDataTable
          columns={[
            { key: "createdAt", label: "Zeitpunkt" },
            {
              key: "source",
              label: "Quelle",
              render: (row: ImportJobRow) => importSourceLabel(row)
            },
            { key: "import_typ", label: "Typ", render: (row: ImportJobRow) => importLabel(row.import_typ) },
            { key: "filename", label: "Datei" },
            {
              key: "status",
              label: "Status",
              render: (row: ImportJobRow) => <span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span>
            },
            { key: "valid_rows", label: "Valide", align: "center" as const },
            { key: "invalid_rows", label: "Fehler", align: "center" as const }
          ]}
          rows={importsData?.data ?? []}
          total={importsData?.meta.total ?? 0}
          page={importPage}
          pageSize={importsData?.meta.pageSize ?? 25}
          onPageChange={setImportPage}
        />
        </div> : null}
      </section>

      {faciliooEnabled ? <section className="panel logs-v2-section">
        <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Facilioo Syncronisationen</h2>
          <button type="button" onClick={() => toggleSection("faciliooSync")} aria-expanded={!collapsedSections.faciliooSync}>
            {collapsedSections.faciliooSync ? "Ausklappen" : "Einklappen"}
          </button>
        </div>
        {!collapsedSections.faciliooSync ? <div style={{ marginTop: 12 }}>
        <div className="logs-filter-row">
          <div className="imports-type-toggle" role="tablist" aria-label="Filter Status">
            <button
              type="button"
              className={syncStatusFilter === "all" ? "is-active" : ""}
              onClick={() => {
                setSyncStatusFilter("all");
                setSyncPage(1);
              }}
            >
              Alle Status
            </button>
            <button
              type="button"
              className={syncStatusFilter === "ok" ? "is-active" : ""}
              onClick={() => {
                setSyncStatusFilter("ok");
                setSyncPage(1);
              }}
            >
              OK
            </button>
            <button
              type="button"
              className={syncStatusFilter === "error" ? "is-active" : ""}
              onClick={() => {
                setSyncStatusFilter("error");
                setSyncPage(1);
              }}
            >
              Fehler
            </button>
          </div>
          <div className="imports-type-toggle" role="tablist" aria-label="Filter Ausführung">
            <button
              type="button"
              className={syncTriggerFilter === "all" ? "is-active" : ""}
              onClick={() => {
                setSyncTriggerFilter("all");
                setSyncPage(1);
              }}
            >
              Alle Läufe
            </button>
            <button
              type="button"
              className={syncTriggerFilter === "manual" ? "is-active" : ""}
              onClick={() => {
                setSyncTriggerFilter("manual");
                setSyncPage(1);
              }}
            >
              Manuell
            </button>
            <button
              type="button"
              className={syncTriggerFilter === "auto" ? "is-active" : ""}
              onClick={() => {
                setSyncTriggerFilter("auto");
                setSyncPage(1);
              }}
            >
              Automatisch
            </button>
          </div>
        </div>
        <ServerDataTable
          columns={[
            { key: "createdAt", label: "Zeitpunkt" },
            { key: "trigger", label: "Ausführung", render: (row: AuditRow) => faciliooSyncTrigger(row.action) },
            {
              key: "status",
              label: "Status",
              render: (row: AuditRow) => (
                <span className={row.action.endsWith("_failed") ? "badge import-failed" : "badge import-imported"}>
                  {faciliooSyncStatus(row.action)}
                </span>
              )
            },
            { key: "actor_name", label: "Benutzer/System" },
            { key: "entity_id", label: "Lauf-ID" }
          ]}
          rows={faciliooSyncData?.data ?? []}
          total={faciliooSyncData?.meta.total ?? 0}
          page={syncPage}
          pageSize={faciliooSyncData?.meta.pageSize ?? 25}
          onPageChange={setSyncPage}
        />
        </div> : null}
      </section> : null}

      {faciliooEnabled ? <section className="panel logs-v2-section">
        <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Facilioo Webhook Queue</h2>
          <button type="button" onClick={() => toggleSection("faciliooWebhookQueue")} aria-expanded={!collapsedSections.faciliooWebhookQueue}>
            {collapsedSections.faciliooWebhookQueue ? "Ausklappen" : "Einklappen"}
          </button>
        </div>
        {!collapsedSections.faciliooWebhookQueue ? <div style={{ marginTop: 12 }}>
        <div className="logs-filter-row">
          <div className="imports-type-toggle" role="tablist" aria-label="Filter Webhook Status">
            <button
              type="button"
              className={webhookStatusFilter === "all" ? "is-active" : ""}
              onClick={() => {
                setWebhookStatusFilter("all");
                setWebhookPage(1);
              }}
            >
              Alle
            </button>
            <button
              type="button"
              className={webhookStatusFilter === "queued" ? "is-active" : ""}
              onClick={() => {
                setWebhookStatusFilter("queued");
                setWebhookPage(1);
              }}
            >
              Queued
            </button>
            <button
              type="button"
              className={webhookStatusFilter === "deduplicated" ? "is-active" : ""}
              onClick={() => {
                setWebhookStatusFilter("deduplicated");
                setWebhookPage(1);
              }}
            >
              Deduplicated
            </button>
          </div>
        </div>
        <ServerDataTable
          columns={[
            { key: "createdAt", label: "Zeitpunkt" },
            {
              key: "status",
              label: "Status",
              render: (row: FaciliooWebhookQueueRow) => (
                <span className={faciliooWebhookStatusBadgeClass(row)}>
                  {faciliooWebhookStatus(row)}
                </span>
              )
            },
            { key: "dbProcessed", label: "In DB verarbeitet", render: (row: FaciliooWebhookQueueRow) => webhookDbStatusLabel(row) },
            { key: "entityName", label: "Entity", render: (row: FaciliooWebhookQueueRow) => readWebhookChange(row, "entityName") },
            { key: "eventType", label: "Type", render: (row: FaciliooWebhookQueueRow) => readWebhookChange(row, "eventType") },
            { key: "entityId", label: "Entity-ID", render: (row: FaciliooWebhookQueueRow) => readWebhookChange(row, "entityId") },
            { key: "eventId", label: "Event-ID", render: (row: FaciliooWebhookQueueRow) => readWebhookChange(row, "eventId") }
          ]}
          rows={(faciliooWebhookData?.data ?? []) as FaciliooWebhookQueueRow[]}
          total={faciliooWebhookData?.meta.total ?? 0}
          page={webhookPage}
          pageSize={faciliooWebhookData?.meta.pageSize ?? 25}
          onPageChange={setWebhookPage}
        />
        </div> : null}
      </section> : null}

      {faciliooEnabled ? <section className="panel logs-v2-section">
        <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Facilioo Webhook Rejects</h2>
          <button type="button" onClick={() => toggleSection("faciliooWebhookRejects")} aria-expanded={!collapsedSections.faciliooWebhookRejects}>
            {collapsedSections.faciliooWebhookRejects ? "Ausklappen" : "Einklappen"}
          </button>
        </div>
        {!collapsedSections.faciliooWebhookRejects ? <div style={{ marginTop: 12 }}>
        <div className="logs-filter-row">
          <div className="imports-type-toggle" role="tablist" aria-label="Filter Webhook Reject Status">
            <button
              type="button"
              className={webhookRejectStatusFilter === "all" ? "is-active" : ""}
              onClick={() => {
                setWebhookRejectStatusFilter("all");
                setWebhookRejectPage(1);
              }}
            >
              Alle
            </button>
            <button
              type="button"
              className={webhookRejectStatusFilter === "401" ? "is-active" : ""}
              onClick={() => {
                setWebhookRejectStatusFilter("401");
                setWebhookRejectPage(1);
              }}
            >
              401
            </button>
            <button
              type="button"
              className={webhookRejectStatusFilter === "400" ? "is-active" : ""}
              onClick={() => {
                setWebhookRejectStatusFilter("400");
                setWebhookRejectPage(1);
              }}
            >
              400
            </button>
            <button
              type="button"
              className={webhookRejectStatusFilter === "500" ? "is-active" : ""}
              onClick={() => {
                setWebhookRejectStatusFilter("500");
                setWebhookRejectPage(1);
              }}
            >
              500
            </button>
            <button
              type="button"
              className={webhookRejectStatusFilter === "503" ? "is-active" : ""}
              onClick={() => {
                setWebhookRejectStatusFilter("503");
                setWebhookRejectPage(1);
              }}
            >
              503
            </button>
          </div>
        </div>
        <ServerDataTable
          columns={[
            { key: "createdAt", label: "Zeitpunkt" },
            {
              key: "status",
              label: "HTTP",
              align: "center" as const,
              render: (row: FaciliooWebhookRejectRow) => (
                <span className={Number(row.status) >= 500 ? "badge import-failed" : "badge import-validated"}>
                  {String(row.status || "-")}
                </span>
              )
            },
            { key: "reason", label: "Reason" },
            { key: "message", label: "Message" },
            { key: "entityName", label: "Entity", render: (row: FaciliooWebhookRejectRow) => row.entityName || "-" },
            { key: "eventId", label: "Event-ID", render: (row: FaciliooWebhookRejectRow) => row.eventId || "-" },
            { key: "apiKeyPrefix", label: "Key-Präfix", render: (row: FaciliooWebhookRejectRow) => row.apiKeyPrefix || "-" }
          ]}
          rows={faciliooWebhookRejectData?.data ?? []}
          total={faciliooWebhookRejectData?.meta.total ?? 0}
          page={webhookRejectPage}
          pageSize={faciliooWebhookRejectData?.meta.pageSize ?? 25}
          onPageChange={setWebhookRejectPage}
        />
        </div> : null}
      </section> : null}

      <section className="panel logs-v2-section">
        <div className="logs-v2-section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Änderungsprotokoll</h2>
          <button type="button" onClick={() => toggleSection("audit")} aria-expanded={!collapsedSections.audit}>
            {collapsedSections.audit ? "Ausklappen" : "Einklappen"}
          </button>
        </div>
        {!collapsedSections.audit ? <div style={{ marginTop: 12 }}>
        <ServerDataTable
          columns={[
            { key: "createdAt", label: "Zeitpunkt" },
            { key: "actor_name", label: "Benutzer" },
            { key: "entity_type", label: "Datensatztyp" },
            { key: "entity_id", label: "Datensatz-ID" },
            { key: "action", label: "Aktion" },
            {
              key: "object_ref",
              label: "Objektbezug",
              render: (row: AuditRow) => {
                const ref = normalizeMojibake(String(row.object_ref ?? "-"));
                const objectId = String((row as unknown as { object_id?: string; objectId?: string }).object_id ?? (row as unknown as { object_id?: string; objectId?: string }).objectId ?? "").trim();
                if (objectId) {
                  return (
                    <Link className="badge object-number" to={`/objects/${objectId}`} title={ref}>
                      {ref}
                    </Link>
                  );
                }
                if (ref !== "-") {
                  return (
                    <Link className="badge object-number" to={`/objects?search=${encodeURIComponent(ref)}`} title={ref}>
                      {ref}
                    </Link>
                  );
                }
                return <span className="badge object-number">-</span>;
              }
            }
          ]}
          rows={auditData?.data ?? []}
          total={auditData?.meta.total ?? 0}
          page={auditPage}
          pageSize={auditData?.meta.pageSize ?? 50}
          onPageChange={setAuditPage}
        />
        </div> : null}
      </section>
    </div>
  );
};
